import { ChevronDown, ChevronUp, Plus, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { PollMarkdownContent } from "./poll-markdown-content";
import { PollParticipantSummary } from "./poll-participant-summary";
import { OptionNoteButton } from "./option-note-button";
import { Button } from "../ui/button";
import {
  BASIC_POLL_STATUSES,
  BASIC_POLL_VOTE_TYPES,
  MAX_WRITE_IN_LENGTH,
} from "../../lib/basic-polls/constants";
import { RESULTS_VIEW_MODES, getResultsViewModel } from "../../lib/basic-polls/results-view-model";
import { PollResultsPanel } from "./poll-results-panel";
import { countVoteDraftSelections, isVoteDraftAtSelectionCap } from "../../lib/basic-polls/vote-draft";
import {
  resolveBasicPollDeadline,
  isBasicPollDeadlineOpen,
} from "../../lib/voting-deadlines";
import { useProfileInteraction } from "../../hooks/useProfileInteraction";
import { useAuth } from "../../app/useAuth";
import { ProfileInteractionModals } from "../ProfileInteractionModals";

export function BasicPollVotingCard({
  poll,
  participantCount = 0,
  voteCount = 0,
  hasSubmitted = false,
  myVote = null,
  draft = {},
  canVote = false,
  cardBusy = false,
  voteError = null,
  isCreator = false,
  lifecycleBusy = false,
  isHighlighted = false,
  parentCancelled = false,
  onSetRef,
  onMoveRankedOption,
  onAddRankedOption,
  onRemoveRankedOption,
  onSelectOption,
  onChangeOtherText,
  onSubmitVote,
  onClearVote,
  onFinalizePoll,
  onReopenPoll,
  onViewOptionNote,
  eligibleUsers = [],
  votedUsers = [],
  pendingUsers = [],
  showVoteProgress = true,
  showVoterIdentities = true,
  // statusKnown signals whether the per-person voted/pending SPLIT is known
  // (vote docs loaded AND permitted). When false, the summary renders a neutral
  // split ("—") instead of a definitive "0/N voted". Additive (default true) so
  // callers that haven't opted in are unaffected.
  statusKnown = true,
  voteIdentityDisplayMode = "named",
  voteVisibilityHint = null,
  // Results view model (group modal opt-in). When omitted, the card preserves
  // legacy behavior: render persisted poll.finalResults only, never deriving a
  // tally from votes. When passed, results are selections-gated via the view
  // model (live tally for OPEN entitled viewers; derived results for FINALIZED
  // entitled viewers even when finalResults is absent).
  votes,
  canReadVoteDetails,
  canReadVoteProgress,
}) {
  const { handleAvatarClick, modalProps } = useProfileInteraction();
  const { user: currentUser } = useAuth();
  const avatarOnClick = showVoterIdentities ? handleAvatarClick : undefined;

  const voteType = poll?.settings?.voteType || BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE;
  const isRanked = voteType === BASIC_POLL_VOTE_TYPES.RANKED_CHOICE;
  const allowMultiple = poll?.settings?.allowMultiple === true;
  const allowWriteIn = poll?.settings?.allowWriteIn === true;
  const maxSelections = Number.isFinite(poll?.settings?.maxSelections)
    ? Math.max(1, Number(poll.settings.maxSelections))
    : null;
  const selectedOptionIds = Array.isArray(draft.optionIds) ? draft.optionIds : [];
  const otherText = String(draft.otherText || "");
  const rankings = Array.isArray(draft.rankings) ? draft.rankings : [];
  const sortedOptions = [...(Array.isArray(poll?.options) ? poll.options : [])].sort((left, right) => {
    const leftOrder = Number.isFinite(left?.order) ? left.order : Number.MAX_SAFE_INTEGER;
    const rightOrder = Number.isFinite(right?.order) ? right.order : Number.MAX_SAFE_INTEGER;
    return leftOrder - rightOrder;
  });
  const optionsById = new Map(
    sortedOptions.filter((option) => option?.id).map((option) => [option.id, option])
  );
  const rankedOptions = rankings
    .map((optionId) => sortedOptions.find((option) => option.id === optionId))
    .filter(Boolean);
  const unrankedOptions = sortedOptions.filter((option) => option?.id && !rankings.includes(option.id));
  const pollStatus = poll?.status || BASIC_POLL_STATUSES.OPEN;
  const isFinalized = pollStatus === BASIC_POLL_STATUSES.FINALIZED;
  const requiredPending = poll?.required && !hasSubmitted;
  const closedAt = resolveBasicPollDeadline(poll);
  // eslint-disable-next-line react-hooks/purity -- intentional: render-time Date.now() read to derive deadline-passed
  const deadlinePassed = !isBasicPollDeadlineOpen(poll, Date.now());
  // Derived display state for the status badge + deadline hint. A poll is
  // "closed" (but not finalized) when its deadline lapsed, its parent session
  // was cancelled, OR it is persisted CLOSED — otherwise it reads as open
  // while it can be voted. Including persisted CLOSED here keeps the badge and
  // the results-tally label in agreement (both read "Closed" / "Results so far").
  const isClosed = !isFinalized && (deadlinePassed || parentCancelled || pollStatus === BASIC_POLL_STATUSES.CLOSED);
  const isOpen = !isFinalized && !isClosed;

  // Results view model. Legacy callers (no permission flags) preserve the old
  // "show persisted finalResults only" behavior. Opted-in callers (group modal)
  // get the full selections-gated model.
  const resultsVM = (() => {
    if (typeof canReadVoteDetails !== "boolean") {
      return {
        mode: poll?.finalResults ? RESULTS_VIEW_MODES.FINAL_RESULTS : RESULTS_VIEW_MODES.HIDDEN,
        results: poll?.finalResults || null,
      };
    }
    return getResultsViewModel({
      poll,
      votes: Array.isArray(votes) ? votes : [],
      canReadVoteDetails,
      canReadVoteProgress: Boolean(canReadVoteProgress),
      isClosed,
    });
  })();
  const hasVisibleResults =
    resultsVM.results &&
    (resultsVM.mode === RESULTS_VIEW_MODES.LIVE_RESULTS ||
      resultsVM.mode === RESULTS_VIEW_MODES.CLOSED_RESULTS ||
      resultsVM.mode === RESULTS_VIEW_MODES.FINAL_RESULTS);

  // maxSelections live accounting. A write-in counts toward the cap.
  const selectionCount = countVoteDraftSelections(draft);
  const atSelectionCap = isVoteDraftAtSelectionCap(draft, { allowMultiple, maxSelections });
  // A write-in counts toward the cap. Block STARTING a new write-in once the cap
  // is reached (but keep an existing write-in editable so it can be changed/cleared)
  // so the textarea matches the checkboxes, which disable at the cap.
  const writeInBlockedAtCap =
    allowMultiple && maxSelections ? atSelectionCap && otherText.trim().length === 0 : false;

  return (
    <>
    <div
      key={poll?.id}
      id={poll?.id ? `embedded-poll-${poll.id}` : undefined}
      ref={onSetRef}
      className={`rounded-2xl border bg-card p-4 transition-all ${
        isHighlighted
          ? "border-brand-primary ring-2 ring-brand-primary/30"
          : "border-border"
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-card-foreground" title={poll?.title || "Untitled poll"}>
              {poll?.title || "Untitled poll"}
            </h4>
            <span className="rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
              {isRanked ? "Ranked choice" : "Multiple choice"}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                requiredPending
                  ? "border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-700/70 dark:bg-amber-900/30 dark:text-amber-200"
                  : poll?.required
                    ? "border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-700/70 dark:bg-amber-900/30 dark:text-amber-200"
                    : "border border-border text-muted-foreground"
              }`}
            >
              {poll?.required ? "Required" : "Optional"}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                isFinalized
                  ? "border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700/70 dark:bg-emerald-900/30 dark:text-emerald-200"
                  : "border border-border text-muted-foreground"
              }`}
            >
              {isFinalized ? "Finalized" : isClosed ? "Closed" : "Open"}
            </span>
          </div>
          {/* Layer A: the aggregate count is never hidden. Who-voted detail is
              gated elsewhere (the avatar stacks + the voteVisibilityHint), but
              the "N/M voted" count always renders. */}
          <p className="text-xs text-muted-foreground">
            {`${voteCount}/${participantCount} voted`}
          </p>
          {isOpen && closedAt ? (
            <p className="text-[11px] text-muted-foreground">
              {`Closes ${formatDistanceToNow(closedAt, { addSuffix: true })}`}
            </p>
          ) : null}
          <PollParticipantSummary
            eligibleUsers={eligibleUsers}
            votedUsers={votedUsers}
            pendingUsers={pendingUsers}
            eligibleCount={participantCount}
            votedCount={voteCount}
            showPending={!isFinalized}
            showVoteProgress={showVoteProgress}
            statusKnown={statusKnown}
            showVoterIdentities={showVoterIdentities}
            voteIdentityDisplayMode={voteIdentityDisplayMode}
            currentUser={currentUser}
            visibilityHint={voteVisibilityHint}
            onAvatarClick={avatarOnClick}
            className="mt-1"
          />
          {voteVisibilityHint ? (
            <p className="text-[11px] text-muted-foreground">{voteVisibilityHint}</p>
          ) : null}
          <PollMarkdownContent content={poll?.description} />
        </div>
        {isCreator && (onReopenPoll || onFinalizePoll) ? (
          <div className="flex items-center gap-2">
            {isFinalized ? (
              <Button
                type="button"
                variant="outline"
                onClick={onReopenPoll}
                disabled={cardBusy}
                className="h-auto rounded-full px-3 py-1 text-xs"
              >
                {lifecycleBusy ? "Re-opening..." : "Re-open poll"}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={onFinalizePoll}
                disabled={cardBusy}
                className="h-auto rounded-full px-3 py-1 text-xs"
              >
                {lifecycleBusy ? "Finalizing..." : "Finalize poll"}
              </Button>
            )}
          </div>
        ) : null}
      </div>

      {canVote && sortedOptions.length === 0 ? (
        <div className="mt-3 rounded-xl border border-border bg-muted px-3 py-3 text-xs text-muted-foreground">
          <p className="font-semibold">No options to vote on</p>
          <p className="mt-1">This poll has no options yet. Check back later or contact the organizer.</p>
        </div>
      ) : canVote ? (
        <>
          {allowMultiple && maxSelections ? (
            <p className="mt-3 text-xs font-semibold text-muted-foreground">
              Select up to {maxSelections} ({selectionCount}/{maxSelections} selected)
            </p>
          ) : null}
          {isRanked ? (
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Ranked
                </p>
                <div className="mt-2 space-y-2">
                  {rankedOptions.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No rankings yet.</p>
                  ) : (
                    rankedOptions.map((option, index) => (
                      <div
                        key={option.id}
                        className="flex flex-wrap items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm"
                      >
                        <span className="text-xs font-semibold text-muted-foreground">
                          {index + 1}.
                        </span>
                        <span className="min-w-0 flex-1 space-y-1">
                          <span className="block truncate text-card-foreground" title={option.label}>
                            {option.label}
                          </span>
                          {String(option?.note || "").trim() ? (
                            <OptionNoteButton
                              label={option.label}
                              onClick={() => onViewOptionNote?.(poll?.title, option)}
                            />
                          ) : null}
                        </span>
                        <div className="flex shrink-0 flex-wrap gap-1.5">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => onMoveRankedOption?.(option.id, "up")}
                            disabled={cardBusy || index === 0}
                            aria-label={`Move ${option.label} up`}
                            className="min-h-[40px] px-3 text-xs"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => onMoveRankedOption?.(option.id, "down")}
                            disabled={cardBusy || index === rankedOptions.length - 1}
                            aria-label={`Move ${option.label} down`}
                            className="min-h-[40px] px-3 text-xs"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => onRemoveRankedOption?.(option.id)}
                            disabled={cardBusy}
                            aria-label={`Remove ${option.label}`}
                            className="min-h-[40px] px-3 text-xs"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Unranked
                </p>
                <div className="mt-2 space-y-2">
                  {unrankedOptions.map((option) => (
                    <div
                      key={option.id}
                      className="flex min-h-[44px] items-center justify-between gap-2 rounded-xl border border-border px-3 py-2 text-sm"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-card-foreground" title={option.label}>
                          {option.label}
                        </span>
                        {String(option?.note || "").trim() ? (
                          <OptionNoteButton
                            label={option.label}
                            className="mt-1"
                            onClick={() => onViewOptionNote?.(poll?.title, option)}
                          />
                        ) : null}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => onAddRankedOption?.(option.id)}
                        disabled={cardBusy}
                        aria-label={`Rank ${option.label}`}
                        className="min-h-[40px] shrink-0 px-3 text-xs"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {unrankedOptions.length === 0 ? (
                    <p className="text-xs text-emerald-700 dark:text-emerald-300">All options ranked.</p>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {sortedOptions.map((option) => {
                const checked = selectedOptionIds.includes(option.id);
                const disabledBecauseAtCap =
                  allowMultiple && maxSelections ? atSelectionCap && !checked : false;
                return (
                  <div
                    key={option.id}
                    className={`flex min-h-[44px] items-center gap-3 rounded-xl border px-3 py-2 text-sm transition-colors hover:bg-muted focus-within:ring-2 focus-within:ring-ring ${
                      checked ? "border-brand-primary bg-brand-primary/10" : "border-border"
                    }`}
                  >
                    <label className="flex min-w-0 flex-1 items-center gap-3">
                      <input
                        type={allowMultiple ? "checkbox" : "radio"}
                        name={`embedded-${poll?.id}`}
                        checked={checked}
                        onChange={() => onSelectOption?.(option.id)}
                        disabled={cardBusy || disabledBecauseAtCap}
                        className="h-6 w-6 shrink-0 accent-brand-primary"
                      />
                      <span className="min-w-0 flex-1 text-card-foreground" title={option.label}>
                        {option.label}
                      </span>
                    </label>
                    {String(option?.note || "").trim() ? (
                      <OptionNoteButton
                        label={option.label}
                        onClick={() => onViewOptionNote?.(poll?.title, option)}
                      />
                    ) : null}
                  </div>
                );
              })}
              {allowWriteIn ? (
                <label className="block min-h-[44px] rounded-xl border border-border px-3 py-2 text-sm">
                  <span className="mb-1 flex items-center justify-between gap-2 text-xs font-semibold text-muted-foreground">
                    <span>
                      Other
                      {allowMultiple && maxSelections
                        ? writeInBlockedAtCap
                          ? " (selection limit reached)"
                          : " (counts toward your selection limit)"
                        : ""}
                    </span>
                    <span aria-hidden="true" className="tabular-nums">
                      {otherText.length}/{MAX_WRITE_IN_LENGTH}
                    </span>
                  </span>
                  <textarea
                    value={otherText}
                    onChange={(event) => onChangeOtherText?.(event.target.value)}
                    rows={2}
                    maxLength={MAX_WRITE_IN_LENGTH}
                    disabled={cardBusy || writeInBlockedAtCap}
                    aria-disabled={cardBusy || writeInBlockedAtCap}
                    className="w-full rounded-md border border-border bg-card px-2 py-1 text-sm text-card-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </label>
              ) : null}
            </div>
          )}
          {hasSubmitted && myVote ? (
            <p role="status" className="mt-2 text-[11px] text-muted-foreground">
              Your vote is on record.
            </p>
          ) : null}
        </>
      ) : (
        <>
          <p role="status" className="mt-3 text-xs font-semibold text-muted-foreground">
            {isFinalized
              ? "Voting is closed for this poll."
              : parentCancelled
                ? "Voting is closed because this session is cancelled."
                : deadlinePassed
                  ? "Voting is closed because the deadline has passed."
                  : "Voting is closed for this poll."}
          </p>
          {hasVisibleResults ? (
            <PollResultsPanel
              results={resultsVM.results}
              mode={resultsVM.mode}
              optionsById={optionsById}
              sortedOptions={sortedOptions}
              pollId={poll?.id}
              pollTitle={poll?.title}
              onViewOptionNote={onViewOptionNote}
            />
          ) : resultsVM.mode === RESULTS_VIEW_MODES.PROGRESS_ONLY ? null : sortedOptions.length ===
            0 ? (
            <p className="mt-3 text-xs text-muted-foreground">No options to vote on.</p>
          ) : (
            <p className="mt-3 text-xs text-muted-foreground">
              Results are hidden until they are unlocked.
            </p>
          )}
          {myVote ? (
            <p role="status" className="mt-2 text-[11px] text-muted-foreground">
              Your vote is on record.
            </p>
          ) : null}
        </>
      )}

      {/* Live tally for OPEN entitled viewers — SECONDARY to the voting form.
          The panel renders its own bordered "Live results" section. */}
      {canVote && hasVisibleResults && resultsVM.mode === RESULTS_VIEW_MODES.LIVE_RESULTS ? (
        <PollResultsPanel
          results={resultsVM.results}
          mode={resultsVM.mode}
          optionsById={optionsById}
          sortedOptions={sortedOptions}
          pollId={poll?.id}
          pollTitle={poll?.title}
          onViewOptionNote={onViewOptionNote}
        />
      ) : null}

      {canVote && sortedOptions.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            onClick={onSubmitVote}
            disabled={
              cardBusy ||
              (isRanked
                ? rankings.length === 0
                : selectedOptionIds.length === 0 && otherText.trim().length === 0)
            }
            className="rounded-full px-4 py-2 text-xs"
          >
            {cardBusy ? "Saving..." : isRanked ? "Submit ranking" : "Submit vote"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onClearVote}
            disabled={cardBusy}
            className="rounded-full px-4 py-2 text-xs"
          >
            Clear vote
          </Button>
        </div>
      ) : null}

      {voteError ? (
        <p role="alert" className="mt-2 text-xs text-rose-600 dark:text-rose-400">{voteError}</p>
      ) : null}
    </div>
    <ProfileInteractionModals {...modalProps} />
    </>
  );
}
